import { Wallet, Activity, CheckCircle, XCircle, ArrowRight, ListChecks, ShieldCheck } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useVerificationStatsQuery } from "../../hooks/queries/useVerificationQueries";

// ─── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({
  title,
  value,
  icon: Icon,
  colorClass,
  loading,
  to,
}: {
  title: string;
  value: string;
  icon: any;
  colorClass: string;
  loading: boolean;
  to?: string;
}) => {
  const content = (
    <div className="cht-bg-white cht-rounded-squircle cht-shadow-soft cht-p-6 cht-border cht-border-slate-200/80 cht-flex cht-items-center cht-gap-4 cht-transition-all hover:cht-border-[#3e57da]/30">
      <div className={`cht-p-4 cht-rounded-2xl cht-shrink-0 ${colorClass}`}>
        <Icon size={24} />
      </div>
      <div>
        <p className="cht-text-xs cht-text-gray-500 cht-font-bold cht-uppercase cht-tracking-wide">{title}</p>
        {loading ? (
          <div className="cht-h-7 cht-w-20 cht-bg-gray-200 cht-rounded-lg cht-animate-pulse cht-mt-1" />
        ) : (
          <h3 className="cht-text-2xl cht-font-black cht-text-gray-900 cht-mt-0.5">{value}</h3>
        )}
      </div>
    </div>
  );

  return to ? <Link to={to} className="cht-block">{content}</Link> : content;
};

// ─── Status Badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }: { status: string }) => {
  const map: Record<string, string> = {
    Success: "cht-bg-emerald-50 cht-text-emerald-700 cht-border cht-border-emerald-200",
    Failed: "cht-bg-rose-50 cht-text-rose-700 cht-border cht-border-rose-200",
    Pending: "cht-bg-amber-50 cht-text-amber-700 cht-border cht-border-amber-200",
  };
  return (
    <span
      className={`cht-inline-flex cht-px-2.5 cht-py-0.5 cht-rounded-full cht-text-[11px] cht-font-bold ${
        map[status] ?? map.Pending
      }`}
    >
      {status}
    </span>
  );
};

// ─── Recent Log Row ───────────────────────────────────────────────────────────
const LogRow = ({ log }: { log: any }) => (
  <tr className="cht-border-b cht-border-gray-100 hover:cht-bg-gray-50/50 cht-transition-colors" style={{ height: 56 }}>
    <td className="cht-px-5 cht-py-3 cht-text-xs cht-font-bold cht-text-gray-900">{log.service?.name ?? "—"}</td>
    <td className="cht-px-5 cht-py-3 cht-text-xs cht-text-gray-500">{new Date(log.created_at).toLocaleString()}</td>
    <td className="cht-px-5 cht-py-3"><StatusBadge status={log.status} /></td>
  </tr>
);

const SkeletonRow = () => (
  <tr className="cht-border-b cht-border-gray-100 cht-animate-pulse" style={{ height: 56 }}>
    {[1, 2, 3].map((i) => (
      <td key={i} className="cht-px-5 cht-py-3">
        <div className="cht-h-3 cht-bg-gray-200 cht-rounded cht-w-3/4" />
      </td>
    ))}
  </tr>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const Overview = () => {
  const location = useLocation();
  const basePath = location.pathname.includes("/superadmin")
    ? "/superadmin/dashboard/verification"
    : "/staff/dashboard/verification";

  const { data: stats = null, isLoading: loading } = useVerificationStatsQuery();

  const fmt = (n: number | null | undefined) => (n ?? 0).toLocaleString();
  const balance = stats
    ? `₦${parseFloat(stats.balance).toLocaleString("en-NG", { minimumFractionDigits: 2 })}`
    : "₦0.00";

  return (
    <div className="cht-animate-in cht-fade-in cht-slide-in-from-bottom-4 cht-duration-500 cht-space-y-6">
      {/* ── Quick Action Bar ── */}
      <div className="cht-flex cht-flex-col sm:cht-flex-row sm:cht-items-center sm:cht-justify-between cht-gap-4 cht-p-4 sm:cht-p-5 cht-bg-white cht-rounded-squircle cht-shadow-soft cht-border cht-border-slate-200/80">
        <div className="cht-flex cht-items-center cht-gap-3">
          <div className="cht-w-10 cht-h-10 cht-rounded-xl cht-bg-[#3e57da]/10 cht-text-[#3e57da] cht-flex cht-items-center cht-justify-center">
            <ShieldCheck size={20} />
          </div>
          <div>
            <h4 className="cht-text-sm cht-font-bold cht-text-gray-900">Verification Hub</h4>
            <p className="cht-text-xs cht-text-gray-500">Run identity verifications or review wallet float</p>
          </div>
        </div>
        <div className="cht-flex cht-items-center cht-gap-2.5">
          <Link
            to={`${basePath}/services`}
            className="cht-inline-flex cht-items-center cht-gap-1.5 cht-px-4 cht-py-2 cht-rounded-xl cht-bg-[#3e57da] cht-text-white cht-text-xs cht-font-bold hover:cht-bg-[#3248c0] cht-transition-colors cht-shadow-sm"
          >
            <ListChecks size={14} />
            <span>Launch Service</span>
          </Link>
          <Link
            to={`${basePath}/wallet`}
            className="cht-inline-flex cht-items-center cht-gap-1.5 cht-px-4 cht-py-2 cht-rounded-xl cht-border cht-border-gray-200 cht-text-gray-700 cht-text-xs cht-font-bold hover:cht-bg-gray-50 cht-transition-colors"
          >
            <Wallet size={14} />
            <span>Wallet & Balance</span>
          </Link>
        </div>
      </div>

      {/* ── Stat Cards ── */}
      <div className="cht-grid cht-grid-cols-1 md:cht-grid-cols-2 lg:cht-grid-cols-4 cht-gap-4 sm:cht-gap-6">
        <StatCard
          title="Wallet Balance"
          value={balance}
          icon={Wallet}
          colorClass="cht-bg-blue-50 cht-text-[#3e57da]"
          loading={loading}
          to={`${basePath}/wallet`}
        />
        <StatCard
          title="Total Requests"
          value={fmt(stats?.total)}
          icon={Activity}
          colorClass="cht-bg-purple-50 cht-text-purple-600"
          loading={loading}
          to={`${basePath}/logs`}
        />
        <StatCard
          title="Successful"
          value={fmt(stats?.success)}
          icon={CheckCircle}
          colorClass="cht-bg-emerald-50 cht-text-emerald-600"
          loading={loading}
          to={`${basePath}/logs`}
        />
        <StatCard
          title="Failed"
          value={fmt(stats?.failed)}
          icon={XCircle}
          colorClass="cht-bg-rose-50 cht-text-rose-600"
          loading={loading}
          to={`${basePath}/logs`}
        />
      </div>

      {/* ── Recent Verifications ── */}
      <div className="cht-bg-white cht-rounded-squircle cht-shadow-soft cht-border cht-border-slate-200/80 cht-overflow-hidden">
        <div className="cht-px-6 cht-py-4 cht-border-b cht-border-gray-100 cht-flex cht-items-center cht-justify-between">
          <div>
            <h3 className="cht-text-sm cht-font-bold cht-text-gray-900">Recent Verifications</h3>
            <p className="cht-text-xs cht-text-gray-400">Latest execution audit records for your account</p>
          </div>
          <Link
            to={`${basePath}/logs`}
            className="cht-text-xs cht-font-bold cht-text-[#3e57da] hover:cht-underline cht-flex cht-items-center cht-gap-1"
          >
            <span>View All Logs</span>
            <ArrowRight size={13} />
          </Link>
        </div>
        <div className="cht-overflow-x-auto">
          <table className="cht-w-full">
            <thead>
              <tr className="cht-border-b cht-border-gray-100 cht-bg-gray-50/60">
                <th className="cht-px-5 cht-py-3.5 cht-text-left cht-text-xs cht-font-bold cht-text-gray-500 cht-uppercase">Service</th>
                <th className="cht-px-5 cht-py-3.5 cht-text-left cht-text-xs cht-font-bold cht-text-gray-500 cht-uppercase">Date</th>
                <th className="cht-px-5 cht-py-3.5 cht-text-left cht-text-xs cht-font-bold cht-text-gray-500 cht-uppercase">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
                : stats?.recent_logs?.length > 0
                ? stats.recent_logs.map((log: any, i: number) => <LogRow key={log.id || i} log={log} />)
                : (
                  <tr>
                    <td colSpan={3} className="cht-text-center cht-py-14 cht-text-gray-400 cht-text-xs font-medium">
                      No verification logs recorded yet.
                    </td>
                  </tr>
                )
              }
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Overview;
